import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFullInventoryAccess } from '../../hooks/useFullInventoryAccess';

interface InventoryProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Protected route for inventory-related pages
 * Checks both:
 * 1. User is authenticated with Barber or ShopOwner role
 * 2. User has an active Inventory add-on subscription
 * 3. User has inventoryEnabled: true in their profile
 *
 * If conditions are not met, redirects to appropriate page with error state
 */
const InventoryProtectedRoute: React.FC<InventoryProtectedRouteProps> = ({ children }) => {
  const { state: authState } = useAuth();
  const location = useLocation();
  const { hasSubscription, hasProfileEnabled, isLoading, error } = useFullInventoryAccess();

  // Show loading while checking auth
  if (authState.isLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-black">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!authState.isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role permissions - handle both string and array role formats
  const allowedRoles = ['ShopOwner', 'Barber'];
  const userHasAllowedRole = (): boolean => {
    if (!authState.user?.role) return false;
    const userRole = authState.user.role;
    if (Array.isArray(userRole)) {
      return userRole.some(r => allowedRoles.includes(r));
    }
    return allowedRoles.includes(userRole);
  };

  if (authState.user && !userHasAllowedRole()) {
    return <Navigate to="/unauthorized" replace />;
  }

  // If user doesn't have the subscription, redirect to subscription page
  if (!hasSubscription) {
    return (
      <Navigate
        to="/barber/subscription"
        state={{
          from: location,
          message: 'You need to subscribe to the Inventory add-on to access this feature.',
          type: 'subscription_required'
        }}
        replace
      />
    );
  }

  // If user has subscription but hasn't enabled inventory in profile
  // This should rarely happen since we auto-enable it, but handle it anyway
  if (!hasProfileEnabled) {
    return (
      <Navigate
        to="/barber/profile"
        state={{
          from: location,
          message: 'Please enable Inventory Management in your profile settings.',
          type: 'profile_setting_required'
        }}
        replace
      />
    );
  }

  // If there was an error checking access, show error page
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-black">
        <div className="text-center max-w-md p-8">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Access Check Failed</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // User has full access
  return <>{children}</>;
};

export default InventoryProtectedRoute;
